import { defineComponent as _defineComponent } from 'vue'
import { unref as _unref, resolveComponent as _resolveComponent, createVNode as _createVNode, withCtx as _withCtx, openBlock as _openBlock, createBlock as _createBlock, createCommentVNode as _createCommentVNode, createElementVNode as _createElementVNode, resolveDynamicComponent as _resolveDynamicComponent } from "vue"

const _hoisted_1 = { class: "my-orders-container__type-switcher-wrap" }
const _hoisted_2 = { class: "my-orders-container__actions-right" }

import type { OtTabType } from '@1trading/design-system';
import { computed, onBeforeMount, onMounted, ref } from 'vue';
import { useI18n } from 'vue-i18n';

import persistanceHelper from '@exchange/helpers/persistance-helper';
import { accountService } from '@exchange/libs/account/service/src';
import { authService } from '@exchange/libs/auth/service/src';
import BalancesListExchange from '@exchange/libs/balances/ui/src/lib/balance-list/BalancesListExchange.vue';
import useMQBreakpoints from '@exchange/libs/composables/shared/src/lib/useMQBreakpoints';
import { usePromiseWithCB } from '@exchange/libs/composables/shared/src/lib/usePromise';
import { ordersService } from '@exchange/libs/order/my-orders/service/src';
import { getNumberOfActiveOrdersToCancel } from '@exchange/libs/order/my-orders/ui/src/lib/useOrdersList';
import { MyOrdersListType } from '@exchange/libs/order/shared-model/src/lib/order-essentials';
import { settingsService } from '@exchange/libs/settings/service/src';
import { SubaccountType } from '@exchange/libs/trading-accounts/service/src';

import FilledOrders from './filled/FilledOrders.vue';
import OpenOrders from './open/OpenOrders.vue';
import OpenPositions from './positions/OpenPositions.vue';

type MyOrderTypeSwitcher = OtTabType<MyOrdersListType>;


export default /*#__PURE__*/_defineComponent({
  __name: 'MyOrdersContainer',
  props: {
  dragging: { type: Boolean, default: false },
  marketId: { type: String, required: true },
},
  emits: ["my-orders:scroll-content"],
  setup(__props, { emit: __emit }) {



const emit = __emit;

const tabsPersistance = {
  localstorageKey: 'PRO_MO_ACTIVE_TAB',
  defaultValue: MyOrdersListType.open,
};

const { t } = useI18n({ useScope: 'global' });
const { isXS } = useMQBreakpoints();

const orderNumberToCancel = getNumberOfActiveOrdersToCancel();
const { loading: cancellingAll, createPromise: cancelAllOrders } = usePromiseWithCB(ordersService.cancelAllOrders, true);

const container = ref();
const containerTopPosition = ref(0);
const moveBack = ref(false);

const { subaccountType } = accountService;

const isFuturesOrIsLoadingSubaccount = computed(() => !subaccountType.value || subaccountType.value === SubaccountType.FUTURES);

const myOrderTypeOptions = ref([
  ...(isFuturesOrIsLoadingSubaccount.value
    ? [
        {
          getTitle: () => t('modules.myOrders.tabs.positions'),
          label: computed(() => t('modules.myOrders.tabs.positions')),
          id: MyOrdersListType.positions,
          active: false,
        },
      ]
    : []),
  {
    getTitle: () => t('modules.myOrders.tabs.openOrders'),
    label: computed(() => t('modules.myOrders.tabs.openOrders')),
    id: MyOrdersListType.open,
    active: true,
  },
  {
    getTitle: () => t('modules.myOrders.tabs.filled'),
    label: computed(() => t('modules.myOrders.tabs.filled')),
    id: MyOrdersListType.filled,
    active: false,
  },
  ...(isFuturesOrIsLoadingSubaccount.value
    ? [
        {
          getTitle: () => t('modules.myOrders.tabs.balances'),
          label: computed(() => t('modules.myOrders.tabs.balances')),
          id: MyOrdersListType.balances,
          active: false,
        },
      ]
    : []),
] satisfies Array<MyOrderTypeSwitcher>);

const selectedMyOrderType = ref(myOrderTypeOptions.value.find((e) => e.active));

const showOpenOrders = computed(() => {
  const valueToCompare = !isXS.value ? myOrderTypeOptions.value.find((e) => e.active)?.id : selectedMyOrderType.value?.id;

  return valueToCompare === MyOrdersListType.open;
});
const showAllMarkets = computed(() => Boolean(settingsService.settings.userSettings.myOrdersSettings.showOrdersFromAllMarkets));

const ordersSnapshotsReceived = computed(() => ordersService.snapshotReceived[showOpenOrders.value ? MyOrdersListType.open : MyOrdersListType.filled]);

const isAuthenticated = computed(() => authService.hasBeenAuthenticated);
const loading = computed(() => {
  if (!authService.initialLoadAuthIsChecked) {
    return true;
  }

  if (authService.hasBeenAuthenticated) {
    return !ordersSnapshotsReceived.value;
  }

  return false;
});

const onTabClicked = () => {
  if (isXS.value && container.value?.$el) {
    emit('my-orders:scroll-content', containerTopPosition.value);
  }
};

const onMyOrderTypeSelectClick = () => {
  if (selectedMyOrderType.value) {
    selectedMyOrderType.value.active = true;
  }

  onTabClicked();
};

const onMyOrderTypeTabClick = (tab: MyOrderTypeSwitcher) => {
  moveBack.value = tab.id === MyOrdersListType.open;
  onTabClicked();
};

const updateSettings = async (value: boolean) => {
  await settingsService.setSettings({
    path: 'myOrdersSettings.showOrdersFromAllMarkets',
    value,
  });
};

const setContainerTopPosition = () => {
  const { top } = container.value.$el.getBoundingClientRect();

  containerTopPosition.value = top;
};

const activeComponent = computed(() => {
  const activeTabId = !isXS.value ? myOrderTypeOptions.value.find((e) => e.active)?.id : selectedMyOrderType.value?.id;

  switch (activeTabId) {
    case MyOrdersListType.open:
      return OpenOrders;
    case MyOrdersListType.filled:
      return FilledOrders;
    case MyOrdersListType.balances:
      return BalancesListExchange;
    case MyOrdersListType.positions:
      return OpenPositions;
    default:
      return OpenOrders; // Default to OpenOrders if no match
  }
});

const ensurePersistedTabIsValid = () => {
  const persistedTab = persistanceHelper.getObjFromJSON(tabsPersistance.localstorageKey, tabsPersistance.defaultValue);

  if (persistedTab && !myOrderTypeOptions.value.find((e) => e.id === persistedTab)) {
    persistanceHelper.localStorageDelete(tabsPersistance.localstorageKey);
  }
};

onBeforeMount(ensurePersistedTabIsValid);

onMounted(setContainerTopPosition);

return (_ctx: any,_cache: any) => {
  const _component_x_list_item = _resolveComponent("x-list-item")!
  const _component_ot_select = _resolveComponent("ot-select")!
  const _component_ot_tabs = _resolveComponent("ot-tabs")!
  const _component_ot_checkbox = _resolveComponent("ot-checkbox")!
  const _component_ot_button_dropdown = _resolveComponent("ot-button-dropdown")!
  const _component_x_button = _resolveComponent("x-button")!
  const _component_ot_transition_fade = _resolveComponent("ot-transition-fade")!
  const _component_ot_module_container = _resolveComponent("ot-module-container")!

  return (_openBlock(), _createBlock(_component_ot_module_container, {
    ref_key: "container",
    ref: container,
    class: "my-orders-container",
    "actions-class": "my-orders-container__actions",
    loading: loading.value,
    "toolbar-class": _unref(isXS) ? 'my-orders-container__toolbar--sticky' : ''
  }, {
    "header-left-actions": _withCtx(() => [
      _createElementVNode("div", _hoisted_1, [
        (_unref(isXS))
          ? (_openBlock(), _createBlock(_component_ot_select, {
              key: 0,
              value: selectedMyOrderType.value,
              "onUpdate:value": [
                _cache[0] || (_cache[0] = ($event: any) => ((selectedMyOrderType).value = $event)),
                onMyOrderTypeSelectClick
              ],
              disabled: !isAuthenticated.value,
              narrow: true,
              options: myOrderTypeOptions.value,
              short: true,
              label: "label",
              variant: "dark-outlined"
            }, {
              "selected-option": _withCtx((selected) => [
                _createVNode(_component_x_list_item, {
                  title: selected.label
                }, null, 8, ["title"])
              ]),
              _: 1
            }, 8, ["value", "disabled", "options"]))
          : (_openBlock(), _createBlock(_component_ot_tabs, {
              key: 1,
              tabs: myOrderTypeOptions.value,
              "onUpdate:tabs": _cache[1] || (_cache[1] = ($event: any) => ((myOrderTypeOptions).value = $event)),
              disabled: !isAuthenticated.value,
              persistance: tabsPersistance,
              dense: true,
              onTabSelected: onMyOrderTypeTabClick
            }, null, 8, ["tabs", "disabled"]))
      ])
    ]),
    "header-actions": _withCtx(() => [
      _createElementVNode("div", _hoisted_2, [
        (activeComponent.value !== BalancesListExchange)
          ? (_openBlock(), _createBlock(_component_ot_checkbox, {
              key: 0,
              class: "my-orders-container__show-all",
              selected: showAllMarkets.value,
              label: _ctx.$t('modules.myOrders.settings.showAllMarkets'),
              disabled: !isAuthenticated.value,
              "vertically-centered": true,
              "onUpdate:selected": updateSettings
            }, null, 8, ["selected", "label", "disabled"]))
          : _createCommentVNode("", true),
        (activeComponent.value === OpenOrders && _unref(isXS))
          ? (_openBlock(), _createBlock(_component_ot_button_dropdown, {
              key: 1,
              class: "my-orders-container__cancel-all-dropdown",
              "activator-icon-rotated": true,
              disabled: !showOpenOrders.value || !isAuthenticated.value,
              size: "3xs",
              variant: "icon"
            }, {
              options: _withCtx(() => [
                _createVNode(_component_x_list_item, {
                  title: _ctx.$t('modules.myOrders.cancelAllOrders'),
                  onClick: _unref(cancelAllOrders)
                }, null, 8, ["title", "onClick"])
              ]),
              _: 1
            }, 8, ["disabled"]))
          : (activeComponent.value === OpenOrders)
            ? (_openBlock(), _createBlock(_component_x_button, {
                key: 2,
                class: "my-orders-container__cancel-all-button",
                variant: "tertiary",
                size: "2xs",
                loading: _unref(cancellingAll),
                disabled: !_unref(orderNumberToCancel) || !showOpenOrders.value || !isAuthenticated.value,
                text: _ctx.$t('modules.myOrders.cancelAllOrders'),
                onClick: _unref(cancelAllOrders)
              }, null, 8, ["loading", "disabled", "text", "onClick"]))
            : _createCommentVNode("", true)
      ])
    ]),
    "main-content": _withCtx(() => [
      _createVNode(_component_ot_transition_fade, {
        "move-back": moveBack.value,
        push: true,
        "is-xs": false
      }, {
        default: _withCtx(() => [
          (_openBlock(), _createBlock(_resolveDynamicComponent(activeComponent.value), {
            "history-source": { isInstant: false },
            dragging: __props.dragging,
            "market-id": __props.marketId,
            "show-all-markets": showAllMarkets.value,
            "authenticated-user": isAuthenticated.value
          }, null, 8, ["dragging", "market-id", "show-all-markets", "authenticated-user"]))
        ]),
        _: 1
      }, 8, ["move-back"])
    ]),
    _: 1
  }, 8, ["loading", "toolbar-class"]))
}
}

})